import { ArrowLeftIcon, Button, ButtonLink, CheckmarkCircleIcon } from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { firstDueDateFor, hasBillingAmount, RosterRow, validateRow } from './types';

interface Step3ReviewProps {
  stepper?: ReactNode;
  roster: RosterRow[];
  organizationName?: string;
  isSaving: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

const FREQ_WORD: Record<RosterRow['billingFrequency'], string> = {
  weekly: 'addFamilies.review.frequency.weekly',
  biweekly: 'addFamilies.review.frequency.biweekly',
  monthly: 'addFamilies.review.frequency.monthly',
};

/**
 * Step 3, Review & confirm. An honest pre-flight of what "Add" will actually do:
 * students enrolled, the parent invite that really sends, and the recurring
 * invoices this creates (the consequence that matters most). No editable email —
 * that composer was a stub whose custom body never sent; the invite that goes out
 * is the platform's standard account-setup email.
 */
export function Step3Review(p: Step3ReviewProps) {
  const { t } = useTranslation();
  const { valid, skipped } = useMemo(() => {
    const validations = p.roster.map((r) => ({ r, v: validateRow(r) }));
    return {
      valid: validations.filter((x) => !x.v.isBlank && x.v.errors.length === 0).map((x) => x.r),
      skipped: validations
        .filter((x) => !x.v.isBlank && x.v.errors.length)
        .map((x) => ({ r: x.r, errors: x.v.errors })),
    };
  }, [p.roster]);

  const studentNoun = t('addFamilies.review.studentNoun', { count: valid.length });

  // Enrolled-as-of date. Everyone in a run usually shares one start date (the
  // billing step applies one plan start to all); if they differ, stay generic.
  const enrolledOn = useMemo(() => {
    const dates = valid.map((r) => r.startDate).filter(Boolean);
    const allSame = dates.length > 0 && dates.every((d) => d === dates[0]);
    return allSame ? dayjs(dates[0]).format('MMMM D, YYYY') : t('addFamilies.review.startDateFallback');
  }, [valid, t]);

  // Invoice validation — the recurring billing this confirm will create. Reads
  // the per-family amounts/frequency/due date set on the billing step.
  const billingSummary = useMemo(() => {
    const amount = (v: string) => Number((v || '').replace(/[^0-9.]/g, '')) || 0;
    const billed = valid.filter((r) => hasBillingAmount(r));
    if (billed.length === 0) return { billedCount: 0, unbilled: valid.length, line: '' };

    const total = billed.reduce((sum, r) => sum + amount(r.billingAmount), 0);
    const freq = billed[0].billingFrequency ?? 'monthly';
    const freqWord = t(FREQ_WORD[freq]);
    const period =
      freq === 'monthly'
        ? t('addFamilies.review.period.month')
        : freq === 'weekly'
          ? t('addFamilies.review.period.week')
          : t('addFamilies.review.period.generic');
    // State the actual first due date, computed from the recurring day the
    // provider picked, rather than echoing "the 1st" and leaving them to work
    // out when the first bill actually goes.
    const firstDue = firstDueDateFor(billed[0].dueDate ?? '', freq);
    const dueText = firstDue ? dayjs(firstDue).format('dddd, MMMM D, YYYY') : '';

    const base = t('addFamilies.review.billingSummary', {
      count: billed.length,
      freq: freqWord,
      total: `$${total.toLocaleString()}`,
    });
    const when = dueText
      ? ' ' +
        t('addFamilies.review.firstInvoiceSummary', {
          dueText,
          dir:
            (billed[0].billFor ?? 'next') === 'next'
              ? t('addFamilies.review.direction.upcoming')
              : t('addFamilies.review.direction.previous'),
          period,
        })
      : '';
    return { billedCount: billed.length, unbilled: valid.length - billed.length, line: `${base}${when}` };
  }, [valid, t]);

  return (
    <section className="space-y-6 rounded-lg border border-gray-200 bg-white p-6" data-testid="af-step-review">
      {p.stepper && <div className="border-b border-gray-200 pb-4">{p.stepper}</div>}
      <div>
        <h2 className="text-2xl font-bold text-navy-950">{t('addFamilies.review.title')}</h2>
        <p className="text-muted mt-1 text-sm">{t('addFamilies.review.description')}</p>
      </div>

      {skipped.length > 0 && (
        <div className="rounded-md border border-red-300/60 bg-red-50/40 p-3 text-sm">
          <div className="font-semibold text-red-900">
            {t('addFamilies.review.skippedStudents', { count: skipped.length })}
          </div>
          <ul className="mt-2 space-y-1 text-red-900/80">
            {skipped.map(({ r, errors }, i) => {
              const label =
                (r.childFirstName + ' ' + r.childLastName).trim() ||
                r.parentEmail ||
                t('addFamilies.review.unnamedStudent');
              return (
                <li key={i}>
                  {label}, {errors.join(', ')}
                </li>
              );
            })}
          </ul>
          <button onClick={p.onBack} className="mt-2 text-blue-900 hover:underline">
            ← {t('addFamilies.review.backAndFixAction')}
          </button>
        </div>
      )}

      {/* What confirm will actually do — students enrolled, the invite that sends,
          and the recurring invoices this creates. */}
      <div className="space-y-3 rounded-md border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-navy-950">{t('addFamilies.review.effectsHeading')}</h3>
        <ul className="space-y-3">
          <li className="flex gap-3">
            <CheckmarkCircleIcon className="mt-0.5 size-5 shrink-0 text-green-900" />
            <span className="text-sm text-gray-900">
              {t('addFamilies.review.rosterEffect', {
                count: valid.length,
                date: enrolledOn,
              })}
            </span>
          </li>
          <li className="flex gap-3">
            <CheckmarkCircleIcon className="mt-0.5 size-5 shrink-0 text-green-900" />
            <span className="text-sm text-gray-900">{t('addFamilies.review.inviteEffect')}</span>
          </li>
          {billingSummary.billedCount > 0 && (
            <li className="flex gap-3">
              <CheckmarkCircleIcon className="mt-0.5 size-5 shrink-0 text-green-900" />
              <span className="text-sm text-gray-900">{billingSummary.line}</span>
            </li>
          )}
          {billingSummary.unbilled > 0 && (
            <li className="flex gap-3">
              <CheckmarkCircleIcon className="text-muted mt-0.5 size-5 shrink-0" />
              <span className="text-muted text-sm">
                {t('addFamilies.review.noRateEffect', {
                  count: billingSummary.unbilled,
                })}
              </span>
            </li>
          )}
        </ul>
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
        <ButtonLink
          content={t('addFamilies.review.backAction')}
          iconDirection="left"
          icon={<ArrowLeftIcon className="size-4" />}
          onClick={p.onBack}
        />
        <Button primary disabled={valid.length === 0 || p.isSaving} loading={p.isSaving} onClick={p.onConfirm}>
          {p.isSaving
            ? t('addFamilies.review.addingLabel')
            : t('addFamilies.review.addAction', {
                count: valid.length,
                noun: studentNoun,
              })}
        </Button>
      </div>
    </section>
  );
}
